import { getVersion } from "../helpers/version";
import { processCommandQueue } from "utils";
import type { ExposedPlugins } from "../init";

type FandomAdsCommand = (fandomAds: FandomAds) => void;

/**
 * FandomAds is a class that is used to interact with the ad engine from outside of it.
 * It is used to expose the plugins to the global scope and to provide a way to interact with them.
 *
 * Example usage:
 * ```
 * window.fandomAds = window.fandomAds || {
 *     cmd: [],
 * };
 * window.fandomAds.cmd.push((fandomAds) => {
 *    // you can use provided fandomAds instance to get the plugin or window.fandomAds global variable
 *    const testPlugin = window.fandomAds.getPlugin("TestPlugin");
 *    const testPlugin2 = fandomAds.getPlugin("TestPlugin"); // this works too
 *    testPlugin.callMe("123");
 * });
 * ```
 */
export class FandomAds {
  readonly #plugins: ExposedPlugins;
  public readonly version?: string;

  constructor(
    plugins: ExposedPlugins,
    public cmd: FandomAdsCommand[] = [],
  ) {
    this.#plugins = plugins;
    this.version = getVersion();
    window.fandomAds = this;
    processCommandQueue(this);
  }

  getPlugin<TName extends keyof ExposedPlugins>(
    name: TName,
  ): ExposedPlugins[TName]["api"] {
    if (!this.#plugins[name]) {
      throw new Error(`Plugin ${name as string} does not exist`);
    }

    return this.#plugins[name].api;
  }
}
